import React from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  background: #FFFDF7;
  padding: 100px 0;
  @media (min-width: 768px) { padding: 140px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Layout = styled.div`
  display: grid;
  gap: 80px;
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 120px;
    align-items: start;
  }
`;

const Left = styled.div``;

const Eyebrow = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  color: #C07020;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 300;
  font-style: italic;
  color: #1A1208;
  line-height: 1.1;
  letter-spacing: -0.01em;
  margin-bottom: 28px;
`;

const Rule = styled.div`
  width: 48px;
  height: 1px;
  background: #C07020;
  margin-bottom: 28px;
`;

const Lead = styled.p`
  font-size: 0.95rem;
  font-weight: 300;
  color: rgba(26,18,8,0.65);
  line-height: 1.9;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid rgba(26,18,8,0.1);
`;

const CertGroup = styled.div`
  padding: 32px 0;
  border-bottom: 1px solid rgba(26,18,8,0.08);
  transition: opacity 0.9s ease, transform 0.9s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(16px);`}
`;

const GroupLabel = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.62rem;
  letter-spacing: 0.2em;
  color: #C07020;
  margin-bottom: 14px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Badge = styled.span`
  font-family: 'Raleway', serif;
  font-size: 0.65rem;
  letter-spacing: 0.13em;
  color: rgba(26,18,8,0.72);
  border: 1px solid rgba(26,18,8,0.15);
  padding: 6px 16px;
  border-radius: 999px;
`;

const certGroups = [
  { en: 'Food Safety',           es: 'Inocuidad',             fr: 'Sécurité Alimentaire',    badges: ['HACCP', 'PrimusGFS', 'BPM'] },
  { en: 'Regulatory Compliance', es: 'Cumplimiento Normativo', fr: 'Conformité Réglementaire', badges: ['FDA', 'EU', 'COFEPRIS', 'SENASICA'] },
  { en: 'Sustainability',        es: 'Sostenibilidad',         fr: 'Durabilité',               badges: ['SCS Global', 'Regenerative Organic Certified™'] },
  { en: 'Business Trust',        es: 'Confianza Empresarial',  fr: 'Confiance Commerciale',    badges: ['D-U-N-S®'] },
];

export function CompanyBanner() {
  const { t, lang } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <Section>
      <Container ref={ref} $inView={isInView}>
        <Layout>
          <Left>
            <Eyebrow>{t('Grupo Hacienda Real', 'Grupo Hacienda Real', 'Grupo Hacienda Real')}</Eyebrow>
            <Title>
              {t(
                'Vertical Integration\n& Global Private Label',
                'Integración Vertical\n& Maquila Global',
                'Intégration Verticale\n& Marque Privée Mondiale'
              )}
            </Title>
            <Rule />
            <Lead>
              {t(
                'Agro-industrial consortium with full vertical integration — from seed, irrigation and animal nutrition to pure ranch milk. Specialists in premium cajeta, condensed milk, cheeses and ice cream. Our preservative-free formula makes us unique in the global market. We offer private label (maquila) and industrial supply with full traceability from origin to national and international retail.',
                'Consorcio agroindustrial de integración vertical. Controlamos desde la semilla, riego y nutrición animal para producir leche de rancho pura. Especialistas en cajeta premium, leche condensada, quesos y helados. Nuestra fórmula sin conservadores nos hace únicos en el mercado global. Ofrecemos maquila (private label) y suministro industrial con trazabilidad total desde el origen hasta el retail nacional e internacional.',
                "Consortium agro-industriel à intégration verticale complète — de la graine, l'irrigation et la nutrition animale jusqu'au lait pur de ranch. Spécialistes en cajeta premium, lait concentré, fromages et glaces. Notre formule sans conservateurs nous rend uniques sur le marché mondial. Nous offrons la marque privée (maquila) et l'approvisionnement industriel avec traçabilité totale de l'origine au commerce national et international."
              )}
            </Lead>
          </Left>

          <Right>
            {certGroups.map((g, i) => (
              <CertGroup key={i} $inView={isInView} $delay={i * 100}>
                <GroupLabel>{lang === 'es' ? g.es : lang === 'fr' ? g.fr : g.en}</GroupLabel>
                <BadgeRow>
                  {g.badges.map((b) => <Badge key={b}>{b}</Badge>)}
                </BadgeRow>
              </CertGroup>
            ))}
          </Right>
        </Layout>
      </Container>
    </Section>
  );
}
